import React, { useMemo } from 'react';
import { hooks } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { makeStyles, Theme } from '@material-ui/core';
import SettingDropdown from './SettingDropdown';
import { getDevicesByKind, setAudioOutput } from 'utils/deviceUtils';
import RingTone from 'components/Ringer/RingTone';
import { RootState } from 'store/reducers';
import { toggleAudioTest } from 'actions/AvSettingsModal';
import DropdownLabel from 'components/Dropdown/DropdownLabel';

interface Props {
  intl: { formatMessage: Function };
  wide?: boolean;
  isJoinFlowCall?: boolean;
  handleConfigChange: Function;
}

const messages = defineMessages({
  inputSpeakers: { id: 'components.avSettings.inputLabel.Speakers', defaultMessage: 'Speakers' },
  inputSpeakersSelect: {
    id: 'components.avSettings.inputLabel.SpeakersSelect',
    defaultMessage: 'Select your speakers'
  }
});

export const SpeakerDropdown = (props: Props) => {
  const { intl, isJoinFlowCall, handleConfigChange, wide } = props;
  const isTestingSpeakers = useSelector((state: RootState) => state?.app?.avSettings?.audioTesting);
  const devices = hooks.useDevices();
  const dispatch = useDispatch();
  const mediaSettings = useSelector((state: RootState) => state?.app?.mediaSettings);
  const audioOutputDevices = useMemo(() => getDevicesByKind(devices, 'audiooutput'), [devices]);
  const isInCall = hooks.useCallConnected();
  const classes = useStyles();

  const handleSpeakerTest = () => {
    dispatch(toggleAudioTest(true));
  };

  if (!audioOutputDevices.length) return null;

  return (
    <section>
      <DropdownLabel
        error={!(audioOutputDevices?.length > 0)}
        label={intl.formatMessage(isJoinFlowCall ? messages.inputSpeakersSelect : messages.inputSpeakers)}
        dataTestId={'speakersSelectionHeader'}
      />
      <SettingDropdown
        handleChange={(value) => {
          dispatch(toggleAudioTest(false));
          handleConfigChange('speakers', value);
          setAudioOutput(value);
        }}
        items={audioOutputDevices}
        label={intl.formatMessage(messages.inputSpeakers)}
        testIdPrefix="speakers"
        wide={wide}
        value={
          (audioOutputDevices.find((device) => device.value === mediaSettings.speakers) || audioOutputDevices?.[0])
            ?.value
        }
      />
      {!isInCall && (
        <button className={classes.speakerTestBtn} onClick={handleSpeakerTest} data-testid="speaker-test-link">
          <FormattedMessage id="components.avSettingsModal.playTest" defaultMessage="Test my speakers" />
        </button>
      )}
      {!!audioOutputDevices.length && !isInCall && isTestingSpeakers && <RingTone deviceId={mediaSettings.speakers} />}
    </section>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  subHeading: {
    fontSize: '1.2rem'
  },
  speakerTestBtn: {
    alignSelf: 'flex-end',
    color: colors.lifesizeBlue,
    border: 'none',
    marginTop: theme.spacing(0.5),
    outline: 'none',
    paddingRight: 0,
    textAlign: 'right'
  }
}));

export default injectIntl(SpeakerDropdown);
